import { Badge, Button, Dropdown, Grid, Image } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HeaderLogo from "../../../assets/images/header/header_logo.png";
// import { ReactComponent as HamburgerIcon } from "../../../assets/images/header/hamburger.svg";
import { logout } from "../../../apis/utils";
import Hamburger from "../../../assets/images/icons/Hamburger";
import ShopingCart from "../../../assets/images/icons/ShopingCart";
import User from "../../../assets/images/icons/User";
import UserIsLogin from "../../../assets/images/icons/UserIsLogin";
import { LanguageDropdown } from "../../../components";
import CustomDrawer from "../../../components/CustomDrawer";
import { P1 } from "../../../components/NormalFonts";
import { useAuth } from "../../../context/AuthContext";
import { useLayout } from "../../../context/LayoutContext";
import { useScroll } from "../../../context/ScrollContext";
import { useSignOut } from "../../../hooks/Authentication/auth.hooks";
import { useGetCartStatus } from "../../../hooks/Cart/cart.hooks";
import { useGetMessageUnread } from "../../../hooks/MessageUnread/messageunread.hooks";
import { MenuType } from "./data";
import styles from "./style.module.scss";

type CustomMenuType = {};

const HeaderComponent = () => {
  const { useBreakpoint } = Grid;
  const {
    isAuthenticated,
    setAuthModalVisible,
    setLoginForm,
    setRegisterForm,
  } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const { pathname } = useLocation();
  const { mutateAsync: signout } = useSignOut();
  const { getHeaderPadding } = useLayout();
  const { isTop } = useScroll();
  const [showMenuDrawer, setShowMenuDrawer] = useState<boolean>(false);
  const [showProfileDrawer, setShowProfileDrawer] = useState<boolean>(false);
  const [showLanguageDrawer, setShowLanguageDrawer] = useState<boolean>(false);

  const { data: messageUnread } = useGetMessageUnread({
    enabled: isAuthenticated,
  });

  const { data: cartStatus, refetch: refetchCartStatus } = useGetCartStatus({
    enabled: isAuthenticated,
  });

  const MENU: MenuType[] = [
    {
      key: "coaches",
      label: t("header.menu.book"),
      path: "/trainers",
      type: "item",
      visible: true,
    },
    {
      key: "guide",
      label: t("header.menu.guide"),
      path: "/guide",
      type: "item",
      visible: false,
    },
    {
      key: "about",
      label: t("header.menu.about"),
      path: "/news/detail/9",
      type: "item",
      visible: true,
    },
    {
      key: "price",
      label: t("header.menu.price"),
      path: "/news/detail/12",
      type: "item",
      visible: true,
    },

    {
      key: "news",
      label: t("header.menu.news"),
      path: "/news",
      type: "item",
      visible: true,
    },
    {
      key: "faq",
      label: t("header.menu.faq"),
      path: "/faq",
      type: "item",
      visible: true,
    },
    {
      key: "record",
      label: t("header.menu.record"),
      type: "item",
      path: "/record",
      visible: isAuthenticated && !screens.md,
      dot: screens.md ? false : messageUnread?.has_unread_message || false,
    },
    {
      key: "profile",
      label: t("header.menu.personal"),
      path: "/profile",
      type: "item",
      visible: isAuthenticated && !screens.md,
    },
    {
      key: "signout",
      label: t("normal.logout"),
      path: "/signout",
      type: "auth",
      visible: isAuthenticated && !screens.md,
      onClick: async () => {
        try {
          await signout();
          setTimeout(() => {
            logout();
          }, 500);
        } catch (error: any) {}
      },
    },
    {
      key: "signin",
      label: t("normal.login"),
      path: "/",
      type: "auth",
      visible: isAuthenticated === false && !screens.md,
      onClick: () => {
        setAuthModalVisible(true);
        setLoginForm();
      },
    },
    {
      key: "signup",
      label: t("normal.register"),
      path: "/",
      type: "auth",
      visible: isAuthenticated === false && !screens.md,
      onClick: () => {
        setAuthModalVisible(true);
        setRegisterForm();
      },
    },
  ];

  const PROFILE: MenuType[] = [
    {
      key: "record",
      label: t("header.menu.record"),
      type: "item",
      path: "/record",
      visible: isAuthenticated,
      dot: messageUnread?.has_unread_message || false,
    },
    {
      key: "profile",
      label: t("header.menu.personal"),
      path: "/profile",
      type: "item",
      visible: isAuthenticated,
    },
    {
      key: "signout",
      label: t("normal.logout"),
      path: "/signout",
      type: "item",
      visible: isAuthenticated,
      onClick: async () => {
        await signout();
        setTimeout(() => {
          logout();
        }, 500);
      },
    },
    {
      key: "signin",
      label: t("normal.login"),
      path: "/",
      type: "item",
      visible: isAuthenticated === false,
      onClick: () => {
        setLoginForm();
      },
    },
    {
      key: "signup",
      label: t("normal.register"),
      path: "/",
      type: "item",
      visible: isAuthenticated === false,
      onClick: () => {
        setRegisterForm();
      },
    },
  ];

  const getDropdownItem = () => {
    return PROFILE.filter((_profile) => _profile.visible === true).map(
      (_profile) => {
        return {
          key: _profile.key,
          label: (
            <div
              className={`${styles.menuitem} ${
                window.location.pathname === _profile.path ? styles.active : ""
              }`}
              onClick={
                _profile.onClick
                  ? _profile.onClick
                  : () => navigate(_profile.path)
              }
            >
              <Badge dot={_profile.dot}>
                <P1 color="var(--Neutral-dark-70)">{_profile.label}</P1>
              </Badge>
            </div>
          ),
        };
      }
    );
  };

  const onMenuClose = () => {
    setShowMenuDrawer(false);
  };

  const onMenuOpen = () => {
    setShowMenuDrawer(true);
  };

  const onMenuProfileClose = () => {
    setShowProfileDrawer(false);
  };

  const onMenuProfileOpen = () => {
    setShowProfileDrawer(true);
  };

  const handleLogoClick = () => {
    navigate("/", { replace: true });
    const element = document.getElementById("body-container");
    if (element) {
      element.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLoginClick = () => {
    setLoginForm();
    setAuthModalVisible(true);
  };

  const renderLogin = () => {
    if (screens.md) {
      if (!isAuthenticated) {
        return (
          <>
            <Badge dot={cartStatus ? cartStatus : false}>
              <Button
                type={"primary"}
                icon={<ShopingCart style={{ fontSize: 18 }} />}
                onClick={handleLoginClick}
              />
            </Badge>
            <Button
              type={"primary"}
              icon={<User style={{ fontSize: 18 }} />}
              style={{
                background: "#269499",
              }}
              onClick={handleLoginClick}
            />
          </>
        );
      }
      return (
        <>
          <Badge dot={cartStatus ? cartStatus : false}>
            <Button
              type={"primary"}
              icon={<ShopingCart style={{ fontSize: 18 }} />}
              onClick={() => {
                navigate("/cart");
              }}
            />
          </Badge>
          <Dropdown
            menu={{ items: getDropdownItem() }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Badge dot={messageUnread?.has_unread_message || false}>
              <Button
                type={"primary"}
                icon={<UserIsLogin style={{ fontSize: 18 }} />}
                // onClick={() => {
                //   onMenuProfileOpen();
                // }}
              />
            </Badge>
          </Dropdown>
        </>
      );
    }

    return (
      <div className={styles.loginContainer}>
        <Badge dot={cartStatus ? cartStatus : false}>
          <Button
            className={styles.icon}
            size={"small"}
            shape={"circle"}
            type={"primary"}
            icon={
              <ShopingCart
                style={{ color: "var(--primary-90)", fontSize: 24 }}
              />
            }
            onClick={() => {
              if (!isAuthenticated) {
                handleLoginClick();
                return;
              }
              navigate("/cart");
            }}
          />
        </Badge>
      </div>
    );
  };

  const getMenuFontSize = () => {
    if (screens.lg) return "var(--menu-fontsize-lg)";
    if (screens.md) return "var(--menu-fontsize-md)";
    return "var(--menu-fontsize-md)";
  };

  const renderMenu = () => {
    if (screens.md) {
      return MENU.filter(
        (_menu: MenuType & CustomMenuType) => _menu.visible === true
      ).map((_menu: MenuType & CustomMenuType) => {
        return (
          <Link
            style={{ fontSize: getMenuFontSize() }}
            className={`${styles.menu} ${
              window.location.pathname ===
              `${process.env.PUBLIC_URL}${_menu.path}`
                ? styles.active
                : ""
            }`}
            // onClick={() => handleScroll(_menu.key)}
            to={_menu.path}
            key={_menu.key}
          >
            {_menu.label}
          </Link>
        );
      });
    }
    return (
      <Image
        src={HeaderLogo}
        preview={false}
        className={`${styles.logo} ${styles.small}`}
        onClick={handleLogoClick}
      />
    );
  };

  const renderDrawerMenu = () => {
    return MENU.filter(
      (_menu: MenuType & CustomMenuType) => _menu.visible === true
    ).map((_menu: MenuType) => {
      return (
        <Link
          onClick={(e) => {
            if (_menu.onClick) {
              e.preventDefault();
              _menu.onClick();
            }
            setShowMenuDrawer(false);
          }}
          to={_menu.path}
          key={_menu.key}
        >
          <Badge dot={_menu.dot}>
            <P1 color={_menu.type === "item" ? "#1C222B" : "var(--primary)"}>
              {_menu.label}
            </P1>
          </Badge>
        </Link>
      );
    });
  };

  const renderProfileMenu = () => {
    return PROFILE.filter(
      (_menu: MenuType & CustomMenuType) => _menu.visible === true
    ).map((_menu: MenuType) => {
      return (
        <Link
          onClick={() => {
            setShowMenuDrawer(false);
          }}
          to={_menu.path}
          key={_menu.key}
        >
          <P1 color="#1C222B">{_menu.label}</P1>
        </Link>
      );
    });
  };

  const getMenuGap = () => {
    if (screens.xl) return "var(--menu-gap-xl)";
    if (screens.lg) return "var(--menu-gap-lg)";
    if (screens.md) return "var(--menu-gap-md)";
    return "16px";
  };

  useEffect(() => {
    if (!cartStatus) return;
    console.debug("cartStatus", cartStatus);
  }, [cartStatus]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const intervalCartStatus = setInterval(() => {
      refetchCartStatus();
    }, 1000 * 60);

    return () => clearInterval(intervalCartStatus);
  }, [isAuthenticated]);

  return (
    <div
      className={`${styles.headerContainer}`}
      style={{
        padding: getHeaderPadding(),
        background: isTop
          ? "transparent"
          : pathname === "/record"
          ? "var(--bg)"
          : "linear-gradient(180deg, #F0EFEF 55%, rgba(240, 239, 239, 0.00) 100%)",
      }}
      id="header-container"
    >
      <div
        className={`${styles.headerBar} ${screens.lg ? "" : styles.middle} ${
          screens.md ? "" : styles.small
        }`}
      >
        <div className={styles.componentLeft}>
          {screens.md ? (
            <Image
              src={HeaderLogo}
              preview={false}
              className={`${styles.logo} ${screens.xs ? styles.small : ""}`}
              onClick={handleLogoClick}
            />
          ) : (
            <LanguageDropdown
              showDrawer={showLanguageDrawer}
              setShowDrawer={setShowLanguageDrawer}
            />
          )}
        </div>
        <div className={styles.menuWrapper}>
          <div className={styles.leftEdge} />
          <div className={styles.componentCenter} style={{ gap: getMenuGap() }}>
            {renderMenu()}
          </div>
          <div className={styles.rightEdge} />
        </div>

        <div className={styles.componentRight}>
          {renderLogin()}

          {screens.md ? (
            <LanguageDropdown
              showDrawer={showLanguageDrawer}
              setShowDrawer={setShowLanguageDrawer}
            />
          ) : (
            <Badge
              dot={messageUnread?.has_unread_message || false}
              offset={[-1, 1]}
            >
              <Button
                className={styles.icon}
                size={"small"}
                shape={"circle"}
                icon={<Hamburger />}
                type={"primary"}
                onClick={onMenuOpen}
              />
            </Badge>
          )}
        </div>
      </div>
      <CustomDrawer
        placement={"right"}
        closable={true}
        onClose={onMenuClose}
        open={showMenuDrawer}
        width={"100vw"}
        size={"large"}
      >
        <div className={styles.drawerBodyContainer}>{renderDrawerMenu()}</div>
      </CustomDrawer>

      <CustomDrawer
        placement={"right"}
        closable={true}
        onClose={onMenuProfileClose}
        open={showProfileDrawer}
        width={"100vw"}
        size={"large"}
      >
        <div className={styles.drawerBodyContainer}>{renderProfileMenu()}</div>
      </CustomDrawer>
    </div>
  );
};

export default HeaderComponent;
